/**
 * Admin › the rider's wallet (D737b, ADR-0150, doc 32) — the pure view-model behind the "Saldo Vello" card of the passenger record and the
 * approvals inbox, over `@vello/api-client` `RiderWalletAdminApi`.
 *
 * - who may do what (`rider_wallet:view|propose|approve`, all market-scoped GEO words);
 * - the draft check of an adjustment before anything is posted (a positive amount in minor units, a reason the direction admits, a note,
 *   a debit never above the credit the card shows) — the server re-checks all of it and its refusals are worded by
 *   `refusal.admin.rider_wallet` / `problem.rider_wallet`, never mapped here;
 * - the signed balance as a tone ("em crédito" / "em débito" / zero) and the movements as the card draws them;
 * - whether THIS operator may decide a pending adjustment (never the maker; the server refuses `SELF_APPROVAL` anyway — the screen hides
 *   the button and says why), and what the operator is told after proposing (applied at once, or sent for a second operator).
 * Nothing here names a country, a currency or formats an amount: money is formatted by the console's format adapter.
 */
import {
  RIDER_WALLET_REASONS_BY_DIRECTION,
  type RiderWalletAdjustmentView,
  type RiderWalletAdminView,
  type RiderWalletDirectionView,
  type RiderWalletEntryKindView,
  type RiderWalletEntryView,
  type RiderWalletReasonView,
} from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** The longest note the API accepts (`RIDER_WALLET_NOTE_TOO_LONG` beyond it). */
export const RIDER_WALLET_NOTE_MAX = 500;
export const RIDER_WALLET_DIRECTIONS: readonly RiderWalletDirectionView[] = ['CREDIT', 'DEBIT'];

export function riderWalletAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayPropose: boolean; readonly mayApprove: boolean } {
  if (grants === undefined) return { mayView: true, mayPropose: true, mayApprove: true };
  return {
    mayView: grantsAllow(grants, { permission: 'rider_wallet:view', target: 'GEO' }),
    mayPropose: grantsAllow(grants, { permission: 'rider_wallet:propose', target: 'GEO' }),
    mayApprove: grantsAllow(grants, { permission: 'rider_wallet:approve', target: 'GEO' }),
  };
}

/** The reasons a direction admits, in the order the form lists them. */
export function riderWalletReasonsFor(direction: RiderWalletDirectionView): readonly RiderWalletReasonView[] {
  return RIDER_WALLET_REASONS_BY_DIRECTION[direction];
}

export type RiderWalletDraftIssue = 'AMOUNT_REQUIRED' | 'REASON_REQUIRED' | 'NOTE_REQUIRED' | 'NOTE_TOO_LONG' | 'DEBIT_ABOVE_CREDIT';

/** The message id of each draft issue (client-side form checks; a server refusal is worded by the vocabulary). */
export const RIDER_WALLET_DRAFT_MESSAGE: Readonly<Record<RiderWalletDraftIssue, string>> = {
  AMOUNT_REQUIRED: 'rwallet.draft.amount',
  REASON_REQUIRED: 'rwallet.draft.reason',
  NOTE_REQUIRED: 'rwallet.draft.note',
  NOTE_TOO_LONG: 'rwallet.draft.noteLong',
  DEBIT_ABOVE_CREDIT: 'rwallet.draft.debitAboveCredit',
};

/**
 * An adjustment draft: a positive amount (parsed from the money input in minor units), a reason the direction admits, a note (the checker reads why;
 * at most {@link RIDER_WALLET_NOTE_MAX} characters) and, for a debit, no more than the credit the rider has now — a manual debit never creates a debt.
 */
export function riderWalletDraftIssue(d: {
  readonly direction: RiderWalletDirectionView;
  readonly amountMinor: bigint | undefined;
  readonly reasonCode: string;
  readonly note: string;
  readonly creditMinor: bigint;
}): RiderWalletDraftIssue | undefined {
  if (d.amountMinor === undefined || d.amountMinor <= 0n) return 'AMOUNT_REQUIRED';
  if (!(riderWalletReasonsFor(d.direction) as readonly string[]).includes(d.reasonCode)) return 'REASON_REQUIRED';
  const note = d.note.trim();
  if (note === '') return 'NOTE_REQUIRED';
  if (note.length > RIDER_WALLET_NOTE_MAX) return 'NOTE_TOO_LONG';
  if (d.direction === 'DEBIT' && d.amountMinor > d.creditMinor) return 'DEBIT_ABOVE_CREDIT';
  return undefined;
}

/** A rejection needs a reason (`DECISION_REASON_REQUIRED`); an approval may carry none. */
export function riderWalletDecisionIssue(decision: 'APPROVE' | 'REJECT', reason: string): 'REASON_REQUIRED' | 'REASON_TOO_LONG' | undefined {
  const text = reason.trim();
  if (decision === 'REJECT' && text === '') return 'REASON_REQUIRED';
  return text.length > RIDER_WALLET_NOTE_MAX ? 'REASON_TOO_LONG' : undefined;
}

/** Why this operator may not propose on this rider (or undefined when he may): the grant, or his own rider account. */
export function riderWalletProposeBlock(riderId: string, operatorId: string | undefined, mayPropose: boolean): 'NO_PERMISSION' | 'OWN_ACCOUNT' | undefined {
  if (!mayPropose) return 'NO_PERMISSION';
  if (operatorId !== undefined && operatorId === riderId) return 'OWN_ACCOUNT';
  return undefined;
}

/** Why this operator may not decide an adjustment (or undefined when he may): it is not waiting, he lacks the grant, or he proposed it. */
export function riderWalletApproveBlock(
  adjustment: Pick<RiderWalletAdjustmentView, 'proposedBy' | 'status' | 'riderId'>,
  operatorId: string | undefined,
  mayApprove: boolean,
  /** D868: a GLOBAL super administrator decides their own proposal; never one about their own account (OWN_ACCOUNT stays). */
  selfApproval = false,
): 'NOT_PENDING' | 'NO_PERMISSION' | 'OWN_PROPOSAL' | 'OWN_ACCOUNT' | undefined {
  if (adjustment.status !== 'PENDING_APPROVAL') return 'NOT_PENDING';
  if (!mayApprove) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && adjustment.proposedBy === operatorId) return 'OWN_PROPOSAL';
  if (operatorId !== undefined && adjustment.riderId === operatorId) return 'OWN_ACCOUNT';
  return undefined;
}

/** What the operator is told after proposing: the adjustment is on the balance already, or it waits for a second operator. */
export function riderWalletProposeOutcome(a: Pick<RiderWalletAdjustmentView, 'status'>): 'APPLIED' | 'SENT_FOR_APPROVAL' {
  return a.status === 'APPLIED' ? 'APPLIED' : 'SENT_FOR_APPROVAL';
}

export type RiderWalletTone = 'credit' | 'debit' | 'zero';

export interface RiderWalletBalanceModel {
  readonly tone: RiderWalletTone;
  /** The balance as it moves money: positive in credit, negative in debt. */
  readonly signedMinor: bigint;
  /** The magnitude, for a sentence that already says the direction ("R$ 5,00 em débito"). */
  readonly absoluteMinor: bigint;
  readonly creditMinor: bigint;
  readonly debtMinor: bigint;
}

/** The balance as the card draws it: signed, with its tone. The wire already carries credit − debt; the model never recomputes it from a float. */
export function riderWalletBalanceModel(v: Pick<RiderWalletAdminView, 'balanceMinor' | 'creditMinor' | 'debtMinor'>): RiderWalletBalanceModel {
  const tone: RiderWalletTone = v.balanceMinor > 0n ? 'credit' : v.balanceMinor < 0n ? 'debit' : 'zero';
  return { tone, signedMinor: v.balanceMinor, absoluteMinor: v.balanceMinor < 0n ? -v.balanceMinor : v.balanceMinor, creditMinor: v.creditMinor, debtMinor: v.debtMinor };
}

export interface RiderWalletMovementModel {
  readonly id: string;
  readonly kind: RiderWalletEntryKindView;
  readonly at: string;
  /** Signed as it moved the balance. */
  readonly signedMinor: bigint;
  readonly tone: RiderWalletTone;
  /** The adjustment reason to word, only for the team's own credits and debits (a debt's origin is its kind). */
  readonly adjustmentReason: RiderWalletReasonView | undefined;
  readonly rideId: string | undefined;
}

/** The latest movements, newest first (the server already sends them so; sorted again here so a late or merged list never reads out of order). */
export function riderWalletMovements(entries: readonly RiderWalletEntryView[], limit = 50): readonly RiderWalletMovementModel[] {
  return [...entries]
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    .slice(0, limit)
    .map((e) => ({
      id: e.id,
      kind: e.kind,
      at: e.at,
      signedMinor: e.amountMinor,
      tone: e.amountMinor > 0n ? 'credit' : e.amountMinor < 0n ? 'debit' : 'zero',
      adjustmentReason: (e.kind === 'ADJUSTMENT_CREDIT' || e.kind === 'ADJUSTMENT_DEBIT') && isRiderWalletReason(e.reasonCode) ? e.reasonCode : undefined,
      rideId: e.rideId,
    }));
}

const REASONS: readonly string[] = ['GOODWILL', 'SERVICE_RECOVERY', 'CORRECTION', 'CASH_TOP_UP_AT_DESK'];
export function isRiderWalletReason(code: string): code is RiderWalletReasonView {
  return REASONS.includes(code);
}

/** The adjustments awaiting a decision, oldest first (the first to wait is the first to be seen). */
export function pendingAdjustments(rows: readonly RiderWalletAdjustmentView[]): readonly RiderWalletAdjustmentView[] {
  return rows.filter((a) => a.status === 'PENDING_APPROVAL').sort((a, b) => Date.parse(a.proposedAt) - Date.parse(b.proposedAt));
}

/** A refusal sentence without its closing full stop, so it fits inside the screen's own sentence ("It could not be completed: …."). */
export const withoutClosingStop = (text: string): string => text.replace(/[.\s]+$/, '');
